import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Lock, Mail, ShieldCheck } from 'lucide-react';
import Header from '../components/layout/Header';
import ErrorMessage from '../components/common/ErrorMessage';
import { useAuth } from '../components/auth/AuthContext';

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const disabled = !email.trim() || password.length < 4;

  const submit = async (event) => {
    event.preventDefault();
    if (disabled) {
      setError('Renseignez votre mail et votre mot de passe pour continuer.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await login({ email, password });
      if (!result.ok) {
        setError(result.error);
        return;
      }
      navigate(location.state?.from || '/', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <Header />
      <main className="flex-1 pt-10 md:max-w-xl md:mx-auto md:w-full">
        <div className="w-20 h-20 rounded-3xl bg-brand-navy text-white flex items-center justify-center shadow-lg">
          <ShieldCheck className="w-11 h-11" />
        </div>
        <h1 className="mt-8 text-3xl md:text-4xl font-extrabold text-brand-navy">Connexion sécurisée</h1>
        <p className="mt-3 text-text-muted leading-relaxed">Connectez-vous pour accéder à votre tableau de bord, votre historique et votre profil personnel.</p>

        <form onSubmit={submit} className="mt-8 space-y-4" noValidate>
          <label className="block">
            <span className="text-sm font-semibold text-text-primary">Adresse email</span>
            <span className="mt-2 h-14 bg-white border border-gray-200 rounded-xl px-4 flex items-center gap-3 focus-within:border-accent-blue focus-within:ring-2 focus-within:ring-accent-blue/20">
              <Mail className="w-5 h-5 text-text-muted" />
              <input type="email" value={email} onChange={(event) => { setEmail(event.target.value); setError(''); }} placeholder="Votre mail" aria-invalid={Boolean(error)} className="w-full bg-transparent outline-none placeholder-text-subtle" />
            </span>
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-text-primary">Mot de passe</span>
            <span className="mt-2 h-14 bg-white border border-gray-200 rounded-xl px-4 flex items-center gap-3 focus-within:border-accent-blue focus-within:ring-2 focus-within:ring-accent-blue/20">
              <Lock className="w-5 h-5 text-text-muted" />
              <input type="password" value={password} onChange={(event) => { setPassword(event.target.value); setError(''); }} placeholder="Votre mot de passe" className="w-full bg-transparent outline-none" />
            </span>
          </label>
          {error ? <ErrorMessage message={error} /> : null}
          {loading && (
            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl text-sm text-blue-700">
              <svg className="animate-spin h-5 w-5 text-blue-600" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
              Connexion en cours… La première connexion peut prendre quelques secondes.
            </div>
          )}
          <button type="submit" disabled={disabled || loading} className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed">
            {loading ? (
              <><svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg> Connexion…</>
            ) : (
              <>Se connecter <ArrowRight className="w-5 h-5" /></>
            )}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-text-muted">Pas encore de compte ? <Link to="/register" className="text-accent-blue font-semibold">Créer un compte</Link></p>
      </main>
    </div>
  );
}
